import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  HiPaperClip,
  HiXMark,
  HiCheckCircle,
  HiCodeBracket,
  HiCpuChip,
  HiChevronDown,
} from 'react-icons/hi2';
import {
  createSubmission,
  uploadAttachment,
  CATEGORIES,
} from '../../services/productDevelopmentService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

// Dropdown options
export const DROPDOWN_OPTIONS = {
  programmingLanguages: [
    'JavaScript',
    'TypeScript',
    'Python',
    'Java',
    'C#',
    'C++',
    'Go',
    'Rust',
    'PHP',
    'Ruby',
    'Swift',
    'Kotlin',
    'Dart',
    'Scala',
    'R',
    'MATLAB',
    'Other',
  ],
  frameworks: [
    'React',
    'Vue.js',
    'Angular',
    'Next.js',
    'Nuxt.js',
    'Node.js',
    'Express.js',
    'Django',
    'Flask',
    'FastAPI',
    'Spring Boot',
    '.NET Core',
    'Laravel',
    'Ruby on Rails',
    'ASP.NET',
    'Svelte',
    'Ember.js',
    'Other',
  ],
  technologies: [
    'Docker',
    'Kubernetes',
    'Redis',
    'Elasticsearch',
    'GraphQL',
    'WebSocket',
    'RabbitMQ',
    'Apache Kafka',
    'MongoDB',
    'PostgreSQL',
    'MySQL',
    'AWS',
    'Azure',
    'Google Cloud',
    'Terraform',
    'Ansible',
    'Jenkins',
    'GitLab CI/CD',
    'GitHub Actions',
    'Other',
  ],
  hardwareType: [
    'IoT Device',
    'Embedded System',
    'Wearable Device',
    'Industrial Controller',
    'Smart Sensor',
    'Robotics',
    'Automotive Electronics',
    'Medical Device',
    'Consumer Electronics',
    'Food & Beverage Products (Teas, Coffee, etc.)',
    'Personal Care Products (Soaps, Cosmetics, etc.)',
    'Textiles & Apparel',
    'Furniture & Home Goods',
    'Manufactured Goods',
    'Agricultural Products',
    'Handcrafted Items',
    'Packaging & Containers',
    'Other Physical Products',
    'Other',
  ],
  iotCategory: [
    'Edge Computing',
    'Industrial IoT (IIoT)',
    'Consumer IoT',
    'Medical Devices',
    'Automotive IoT',
    'Smart Home',
    'Smart City',
    'Agriculture IoT',
    'Energy Management',
    'Other',
  ],
  industries: [
    'Healthcare',
    'Finance & Banking',
    'E-commerce & Retail',
    'Education',
    'Manufacturing',
    'SaaS',
    'Real Estate',
    'Transportation & Logistics',
    'Energy & Utilities',
    'Telecommunications',
    'Media & Entertainment',
    'Government & Public Sector',
    'Agriculture',
    'Construction',
    'Hospitality & Tourism',
    'Legal Services',
    'Insurance',
    'Non-profit',
    'Food & Beverage',
    'Automotive',
    'Aerospace & Defense',
    'Pharmaceuticals',
    'Biotechnology',
    'Gaming',
    'Fitness & Wellness',
    'Other',
  ],
};

// Software-specific fields - Very Technical
export const SOFTWARE_FIELDS = {
  development: [
    // Core Technical Stack
    {
      id: 'programmingLanguages',
      label: 'Programming Languages Used',
      type: 'multiselect',
      required: true,
      placeholder: 'Select programming languages...',
      options: DROPDOWN_OPTIONS.programmingLanguages,
    },
    {
      id: 'frameworks',
      label: 'Frameworks & Libraries',
      type: 'multiselect',
      required: true,
      placeholder: 'Select frameworks and libraries...',
      options: DROPDOWN_OPTIONS.frameworks,
    },
    {
      id: 'technologies',
      label: 'Technologies & Tools',
      type: 'multiselect',
      required: true,
      placeholder: 'Select technologies and tools...',
      options: DROPDOWN_OPTIONS.technologies,
    },

    // Architecture & Design
    {
      id: 'architecture',
      label: 'System Architecture & Design Patterns',
      type: 'textarea',
      required: true,
      placeholder:
        'Describe architecture (microservices, monolithic, serverless), design patterns used...',
    },
    {
      id: 'requirements',
      label: 'System Requirements & Specifications',
      type: 'textarea',
      required: true,
      placeholder: 'Functional and non-functional requirements, performance specs...',
    },

    // Infrastructure
    {
      id: 'cloudSetup',
      label: 'Cloud Infrastructure & DevOps',
      type: 'textarea',
      required: true,
      placeholder:
        'Cloud provider (AWS, Azure, GCP), CI/CD pipeline, containerization, orchestration...',
    },
    {
      id: 'databases',
      label: 'Database Design & Data Management',
      type: 'textarea',
      required: true,
      placeholder:
        'Database types (SQL/NoSQL), schema design, data modeling, caching strategies...',
    },

    // Integration & APIs
    {
      id: 'apis',
      label: 'APIs & Third-Party Integrations',
      type: 'textarea',
      required: true,
      placeholder: 'REST/GraphQL APIs, third-party services, webhooks, API documentation...',
    },
    {
      id: 'integration',
      label: 'System Integration & Communication',
      type: 'textarea',
      required: false,
      placeholder: 'Message queues, event-driven architecture, service mesh...',
    },

    // Frontend & UX
    {
      id: 'uiux',
      label: 'UI/UX Design & Frontend Implementation',
      type: 'textarea',
      required: true,
      placeholder: 'Design system, component library, responsive design, accessibility...',
    },
    {
      id: 'frontendTech',
      label: 'Frontend Technologies',
      type: 'textarea',
      required: false,
      placeholder: 'React, Vue, Angular, Next.js, styling frameworks, state management...',
    },

    // Security & Compliance
    {
      id: 'security',
      label: 'Security Measures & Compliance',
      type: 'textarea',
      required: true,
      placeholder:
        'Authentication, authorization, encryption, OWASP compliance, GDPR, security audits...',
    },
    {
      id: 'compliance',
      label: 'Regulatory Compliance & Standards',
      type: 'textarea',
      required: false,
      placeholder: 'ISO standards, industry-specific compliance (HIPAA, PCI-DSS)...',
    },

    // Testing & Quality
    {
      id: 'testing',
      label: 'Testing Strategy & Quality Assurance',
      type: 'textarea',
      required: true,
      placeholder: 'Unit tests, integration tests, E2E tests, test coverage, QA processes...',
    },
    {
      id: 'performance',
      label: 'Performance Optimization',
      type: 'textarea',
      required: false,
      placeholder: 'Caching strategies, load balancing, CDN, performance monitoring...',
    },

    // Industry & Application
    {
      id: 'industries',
      label: 'Target Industries & Use Cases',
      type: 'multiselect',
      required: true,
      placeholder: 'Select target industries...',
      options: DROPDOWN_OPTIONS.industries,
    },
    {
      id: 'deployment',
      label: 'Deployment Strategy',
      type: 'textarea',
      required: false,
      placeholder: 'Deployment environments, release strategy, rollback procedures...',
    },

    // Documentation & Maintenance
    {
      id: 'documentation',
      label: 'Technical Documentation',
      type: 'textarea',
      required: false,
      placeholder: 'API docs, code documentation, architecture diagrams, runbooks...',
    },
    {
      id: 'monitoring',
      label: 'Monitoring & Logging',
      type: 'textarea',
      required: false,
      placeholder: 'Application monitoring, error tracking, logging infrastructure, alerting...',
    },
  ],
};

// Non-software product fields - Encompasses hardware, physical products, etc.
export const HARDWARE_FIELDS = {
  development: [
    // Hardware Category
    {
      id: 'hardwareType',
      label: 'Product Type & Category',
      type: 'multiselect',
      required: true,
      placeholder: 'Select product type...',
      options: DROPDOWN_OPTIONS.hardwareType,
    },
    {
      id: 'iotCategory',
      label: 'IoT/Embedded Category',
      type: 'multiselect',
      required: true,
      placeholder: 'Select IoT/Embedded category...',
      options: DROPDOWN_OPTIONS.iotCategory,
    },

    // Circuit & PCB Design
    {
      id: 'schematics',
      label: 'Circuit Schematics & Design',
      type: 'textarea',
      required: true,
      placeholder:
        'Circuit diagrams, component selection rationale, signal flow, power distribution...',
    },
    {
      id: 'pcbDesign',
      label: 'PCB Design & Layout',
      type: 'textarea',
      required: true,
      placeholder:
        'PCB layers, trace routing, component placement, thermal management, EMI considerations...',
    },
    {
      id: 'pcbSpecs',
      label: 'PCB Specifications',
      type: 'textarea',
      required: false,
      placeholder:
        'Board dimensions, layer count, material, thickness, finish, impedance control...',
    },

    // Processing & Control
    {
      id: 'microcontrollers',
      label: 'Microcontrollers & Processors',
      type: 'textarea',
      required: true,
      placeholder:
        'MCU model (ESP32, Arduino, STM32, Raspberry Pi), processor specs, clock speed, memory...',
    },
    {
      id: 'processingArchitecture',
      label: 'Processing Architecture',
      type: 'textarea',
      required: false,
      placeholder: 'ARM, x86, RISC-V, processing cores, instruction set architecture...',
    },

    // Sensors & Actuators
    {
      id: 'sensors',
      label: 'Sensors & Actuators',
      type: 'textarea',
      required: true,
      placeholder:
        'Sensor types (temperature, motion, pressure, proximity), specifications, accuracy, range...',
    },
    {
      id: 'actuators',
      label: 'Actuator Details',
      type: 'textarea',
      required: false,
      placeholder:
        'Motor types, servo motors, relays, solenoids, specifications and control methods...',
    },

    // Connectivity & Communication
    {
      id: 'connectivity',
      label: 'Connectivity & Communication Protocols',
      type: 'textarea',
      required: true,
      placeholder:
        'WiFi, Bluetooth (BLE/Classic), Zigbee, LoRa, Cellular (4G/5G), Ethernet, CAN bus...',
    },
    {
      id: 'communicationProtocols',
      label: 'Communication Protocols',
      type: 'textarea',
      required: false,
      placeholder: 'MQTT, CoAP, HTTP/HTTPS, Modbus, SPI, I2C, UART, RS-485...',
    },
    {
      id: 'networkArchitecture',
      label: 'Network Architecture',
      type: 'textarea',
      required: false,
      placeholder: 'Mesh networks, star topology, gateway configuration, edge computing setup...',
    },

    // Power & Energy
    {
      id: 'powerSystem',
      label: 'Power System Design',
      type: 'textarea',
      required: true,
      placeholder:
        'Power supply type (battery, AC/DC adapter, solar), voltage levels, power consumption, battery life...',
    },
    {
      id: 'powerManagement',
      label: 'Power Management & Efficiency',
      type: 'textarea',
      required: false,
      placeholder:
        'Power optimization strategies, sleep modes, energy harvesting, power regulation...',
    },

    // Firmware & Software
    {
      id: 'firmware',
      label: 'Firmware Structure & Implementation',
      type: 'textarea',
      required: true,
      placeholder:
        'Firmware architecture, RTOS usage, programming language (C/C++), bootloader, OTA updates...',
    },
    {
      id: 'embeddedSoftware',
      label: 'Embedded Software Stack',
      type: 'textarea',
      required: false,
      placeholder:
        'Operating system (FreeRTOS, Zephyr, Linux), drivers, middleware, application layer...',
    },

    // Mechanical & Enclosure
    {
      id: 'mechanicalDesign',
      label: 'Mechanical Design & Enclosure',
      type: 'textarea',
      required: false,
      placeholder: 'Enclosure material, dimensions, IP rating, thermal design, mounting options...',
    },
    {
      id: 'formFactor',
      label: 'Form Factor & Physical Specifications',
      type: 'textarea',
      required: false,
      placeholder: 'Device dimensions, weight, material selection, environmental ratings...',
    },

    // Prototype & Testing
    {
      id: 'prototypeDetails',
      label: 'Prototype Details & Specifications',
      type: 'textarea',
      required: true,
      placeholder: 'Prototype version, build materials, testing results, performance metrics...',
    },
    {
      id: 'testingValidation',
      label: 'Hardware Testing & Validation',
      type: 'textarea',
      required: false,
      placeholder:
        'Environmental testing, stress testing, compliance testing (FCC, CE), reliability testing...',
    },

    // Industry & Application
    {
      id: 'targetIndustry',
      label: 'Target Industry & Application',
      type: 'multiselect',
      required: true,
      placeholder: 'Select target industries...',
      options: DROPDOWN_OPTIONS.industries,
    },
    {
      id: 'useCases',
      label: 'Specific Use Cases',
      type: 'textarea',
      required: false,
      placeholder: 'Detailed use cases, deployment scenarios, operational requirements...',
    },

    // Compliance & Standards
    {
      id: 'compliance',
      label: 'Regulatory Compliance & Certifications',
      type: 'textarea',
      required: false,
      placeholder:
        'FCC, CE marking, UL certification, industry-specific standards, safety compliance...',
    },
    {
      id: 'standards',
      label: 'Technical Standards & Protocols',
      type: 'textarea',
      required: false,
      placeholder: 'IEEE standards, industry protocols, interoperability standards...',
    },
  ],
};

export const STAGE_FIELDS = {
  idea: [
    { id: 'problemStatement', label: 'Problem Statement', type: 'textarea', required: true },
    { id: 'targetAudience', label: 'Target Audience', type: 'textarea', required: true },
    { id: 'proposedSolution', label: 'Proposed Solution', type: 'textarea', required: true },
    { id: 'marketNeed', label: 'Market Need Validation', type: 'textarea', required: true },
    { id: 'notes', label: 'Additional Notes', type: 'textarea', required: false },
  ],
  research: [
    { id: 'marketSize', label: 'Market Size Analysis', type: 'textarea', required: true },
    { id: 'competitorAnalysis', label: 'Competitor Analysis', type: 'textarea', required: true },
    { id: 'customerNeeds', label: 'Customer Needs Assessment', type: 'textarea', required: true },
    { id: 'marketTrends', label: 'Market Trends', type: 'textarea', required: true },
    { id: 'notes', label: 'Additional Notes', type: 'textarea', required: false },
  ],
  prototype: [
    {
      id: 'prototypeDescription',
      label: 'Prototype Description',
      type: 'textarea',
      required: true,
    },
    { id: 'testingResults', label: 'Testing Results', type: 'textarea', required: true },
    { id: 'userFeedback', label: 'User Feedback', type: 'textarea', required: true },
    { id: 'iterations', label: 'Iterations Made', type: 'textarea', required: true },
    { id: 'notes', label: 'Additional Notes', type: 'textarea', required: false },
  ],
  development: [
    { id: 'features', label: 'Core Features Developed', type: 'textarea', required: true },
    { id: 'milestones', label: 'Milestones Achieved', type: 'textarea', required: true },
    { id: 'challenges', label: 'Challenges Faced', type: 'textarea', required: true },
    { id: 'progress', label: 'Progress Update', type: 'textarea', required: true },
    { id: 'notes', label: 'Additional Notes', type: 'textarea', required: false },
  ],
  mvp: [
    { id: 'mvpFeatures', label: 'MVP Features', type: 'textarea', required: true },
    { id: 'launchPlan', label: 'Launch Plan', type: 'textarea', required: true },
    { id: 'metrics', label: 'Key Metrics to Track', type: 'textarea', required: true },
    {
      id: 'feedbackPlan',
      label: 'User Feedback Collection Plan',
      type: 'textarea',
      required: true,
    },
    { id: 'notes', label: 'Additional Notes', type: 'textarea', required: false },
  ],
};

export const STAGE_NAMES = {
  idea: 'Idea Validation',
  research: 'Market Research',
  prototype: 'Prototype Development',
  development: 'Product Development',
  mvp: 'MVP Launch',
};

// Multi-Select Dropdown Component
const MultiSelectDropdown = ({ field, value, onChange, formData, setFormData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [otherValue, setOtherValue] = useState('');
  const dropdownRef = useRef(null);
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const hasOther = selectedValues.some(v => typeof v === 'string' && v.startsWith('Other:'));
  const otherText = hasOther
    ? selectedValues
        .find(v => typeof v === 'string' && v.startsWith('Other:'))
        ?.replace('Other:', '')
        .trim() || ''
    : '';

  useEffect(() => {
    if (hasOther) {
      setOtherValue(otherText);
    } else {
      setOtherValue('');
    }
  }, [hasOther, otherText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = option => {
    let newValues = [...selectedValues];

    if (option === 'Other') {
      // Toggle "Other" option
      const hasOtherOption = newValues.some(v => typeof v === 'string' && v.startsWith('Other:'));
      if (hasOtherOption) {
        newValues = newValues.filter(v => !(typeof v === 'string' && v.startsWith('Other:')));
        setOtherValue('');
      } else {
        // Add "Other:" placeholder, will be replaced when user types
        newValues.push('Other:');
      }
    } else {
      // Toggle regular option
      const index = newValues.indexOf(option);
      if (index > -1) {
        newValues.splice(index, 1);
      } else {
        newValues.push(option);
      }
    }

    onChange({ target: { name: field.id, value: newValues } });
  };

  const handleOtherInputChange = e => {
    const newOtherValue = e.target.value;
    setOtherValue(newOtherValue);

    let newValues = selectedValues.filter(v => !(typeof v === 'string' && v.startsWith('Other:')));
    if (newOtherValue.trim()) {
      newValues.push(`Other: ${newOtherValue.trim()}`);
    } else {
      // If empty, still keep "Other:" placeholder
      newValues.push('Other:');
    }

    onChange({ target: { name: field.id, value: newValues } });
  };

  const removeOption = option => {
    let newValues = selectedValues.filter(v => {
      if (typeof v === 'string' && v.startsWith('Other:')) {
        return false;
      }
      return v !== option;
    });

    if (typeof option === 'string' && option.startsWith('Other:')) {
      setOtherValue('');
    }

    onChange({ target: { name: field.id, value: newValues } });
  };

  const displayValue =
    selectedValues.length > 0
      ? selectedValues.length === 1
        ? selectedValues[0].replace('Other: ', '')
        : `${selectedValues.length} selected`
      : field.placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
          selectedValues.length > 0 ? 'text-gray-900' : 'text-gray-500'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="truncate">{displayValue}</span>
          <HiChevronDown
            className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-xl"
        >
          <div className="max-h-64 overflow-y-auto p-2">
            {field.options.map(option => {
              const isSelected =
                option === 'Other'
                  ? selectedValues.some(v => typeof v === 'string' && v.startsWith('Other:'))
                  : selectedValues.includes(option);

              return (
                <div key={option}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOption(option)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="flex-1">{option}</span>
                  </label>
                  {option === 'Other' && isSelected && (
                    <div className="px-3 pb-2">
                      <input
                        type="text"
                        value={otherValue}
                        onChange={handleOtherInputChange}
                        placeholder="Specify other option..."
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Selected tags */}
      {selectedValues.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedValues.map((value, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800"
            >
              {value.replace('Other: ', '')}
              <button
                type="button"
                onClick={() => removeOption(value)}
                className="ml-1 text-blue-600 hover:text-blue-800"
              >
                <HiXMark className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const StageSubmissionForm = ({
  stage = 'development',
  category: initialCategory = null,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || CATEGORIES.SOFTWARE);
  const [selectedStage, setSelectedStage] = useState(stage);

  // Reset form when category changes or modal opens/closes
  useEffect(() => {
    if (isOpen && initialCategory) {
      setSelectedCategory(initialCategory);
      setFormData({});
    }
  }, [isOpen, initialCategory]);

  // Get fields based on stage and category - Only show category-specific fields
  const fields = useMemo(() => {
    if (selectedStage === 'development') {
      if (selectedCategory === CATEGORIES.SOFTWARE) {
        return SOFTWARE_FIELDS.development || [];
      } else if (selectedCategory === CATEGORIES.NON_SOFTWARE) {
        return HARDWARE_FIELDS.development || [];
      }
    }
    return STAGE_FIELDS[selectedStage] || [];
  }, [selectedStage, selectedCategory]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileUpload = async e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newAttachments = [];

    for (const file of files) {
      try {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        // For now, we'll store file metadata. Actual upload happens on submit
        newAttachments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
          id: Date.now() + Math.random(),
        });
      } catch (error) {
        toast.error(`Failed to prepare ${file.name}`);
      }
    }

    setAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
    e.target.value = '';
  };

  const removeAttachment = id => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => {
      const value = formData[f.id];
      if (f.type === 'multiselect') {
        return !value || (Array.isArray(value) && value.length === 0);
      }
      return !value?.trim();
    });

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`
      );
      return;
    }

    setLoading(true);
    try {
      // Upload attachments first
      const uploadedAttachments = [];
      for (const attachment of attachments) {
        try {
          const uploaded = await uploadAttachment(
            attachment.file,
            currentUser.uid,
            `temp_${Date.now()}`
          );
          uploadedAttachments.push(uploaded);
        } catch (error) {
          toast.error(`Failed to upload ${attachment.name}`);
        }
      }

      // Create submission
      await createSubmission(
        {
          stage: selectedStage,
          stageName: STAGE_NAMES[selectedStage],
          category: selectedCategory,
          title:
            formData.title ||
            `${STAGE_NAMES[selectedStage]} - ${selectedCategory === CATEGORIES.SOFTWARE ? 'Software' : 'Non-Software'}`,
          description: formData.description || '',
          ...formData,
          attachments: uploadedAttachments,
        },
        currentUser.uid,
        currentUser.email
      );

      toast.success('Submission created successfully');
      setFormData({});
      setAttachments([]);
      setSelectedCategory(CATEGORIES.SOFTWARE);
      setSelectedStage('development');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to create submission');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <div
          className={`sticky top-0 z-10 flex items-center justify-between border-b-2 px-6 py-5 ${
            selectedCategory === CATEGORIES.SOFTWARE
              ? 'border-blue-200 bg-gradient-to-r from-blue-50 to-white'
              : 'border-purple-200 bg-gradient-to-r from-purple-50 to-white'
          }`}
        >
          <div>
            <div className="flex items-center gap-3">
              {selectedCategory === CATEGORIES.SOFTWARE ? (
                <HiCodeBracket className="h-8 w-8 text-blue-600" />
              ) : (
                <HiCpuChip className="h-8 w-8 text-purple-600" />
              )}
              <div>
                <h2 className="text-2xl font-bold text-black">
                  {selectedCategory === CATEGORIES.SOFTWARE ? 'Software' : 'Non-Software'}{' '}
                  Development Submission
                </h2>
                <p className="text-sm text-gray-600">
                  {selectedCategory === CATEGORIES.SOFTWARE
                    ? 'Document your software system architecture, technologies, and implementation'
                    : 'Document your product design, components, manufacturing processes, and specifications'}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600 hover:shadow-md"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Category Badge - Show which category is selected */}
            {selectedCategory && (
              <div
                className={`rounded-xl border-2 p-4 ${
                  selectedCategory === CATEGORIES.SOFTWARE
                    ? 'border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100'
                    : 'border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  {selectedCategory === CATEGORIES.SOFTWARE ? (
                    <HiCodeBracket className="h-6 w-6 text-blue-600" />
                  ) : (
                    <HiCpuChip className="h-6 w-6 text-purple-600" />
                  )}
                  <div>
                    <div
                      className={`font-bold ${
                        selectedCategory === CATEGORIES.SOFTWARE
                          ? 'text-blue-900'
                          : 'text-purple-900'
                      }`}
                    >
                      {selectedCategory === CATEGORIES.SOFTWARE
                        ? 'Software Development'
                        : 'Non-Software Development'}
                    </div>
                    <div className="text-xs text-gray-600">
                      {selectedCategory === CATEGORIES.SOFTWARE
                        ? 'Document your software stack, architecture, and technologies'
                        : 'Document your product design, components, manufacturing processes, and specifications'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Title and Description */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Submission Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title || ''}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="e.g., E-commerce Platform Development"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Development Stage
                </label>
                <select
                  value={selectedStage}
                  onChange={e => setSelectedStage(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  {Object.entries(STAGE_NAMES).map(([key, name]) => (
                    <option key={key} value={key}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Brief Description
              </label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={handleChange}
                rows={2}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Provide a brief overview of your submission..."
              />
            </div>

            {/* Dynamic Fields - Grouped by sections */}
            <div className="space-y-8">
              {fields.map((field, index) => {
                // Group fields visually
                const isNewSection =
                  index === 0 ||
                  (index > 0 &&
                    fields[index - 1].label.split(' ')[0] !== field.label.split(' ')[0]);

                return (
                  <motion.div
                    key={field.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`rounded-xl border ${
                      selectedCategory === CATEGORIES.SOFTWARE
                        ? 'border-blue-100 bg-blue-50/30'
                        : 'border-purple-100 bg-purple-50/30'
                    } p-5`}
                  >
                    <label className="mb-3 block text-sm font-semibold text-gray-800">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {field.type === 'multiselect' ? (
                      <MultiSelectDropdown
                        field={field}
                        value={formData[field.id] || []}
                        onChange={handleChange}
                        formData={formData}
                        setFormData={setFormData}
                      />
                    ) : field.type === 'textarea' ? (
                      <textarea
                        name={field.id}
                        value={formData[field.id] || ''}
                        onChange={handleChange}
                        required={field.required}
                        rows={5}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
                      />
                    ) : (
                      <input
                        type={field.type || 'text'}
                        name={field.id}
                        value={formData[field.id] || ''}
                        onChange={handleChange}
                        required={field.required}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
                      />
                    )}
                    {field.placeholder && field.type !== 'multiselect' && (
                      <p className="mt-2 text-xs italic text-gray-500">{field.placeholder}</p>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* File Attachments */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Attachments</label>
              <div className="rounded-lg border-2 border-dashed border-gray-300 p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2"
                >
                  <HiPaperClip className="h-8 w-8 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {uploading ? 'Uploading...' : 'Click to upload files or drag and drop'}
                  </span>
                  <span className="text-xs text-gray-500">PDF, DOC, DOCX, XLS, XLSX, images</span>
                </label>
              </div>

              {attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  {attachments.map(attachment => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
                    >
                      <div className="flex items-center gap-3">
                        <HiPaperClip className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{attachment.name}</p>
                          <p className="text-xs text-gray-500">
                            {(attachment.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(attachment.id)}
                        className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50"
                      >
                        <HiXMark className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Submitting...
                </>
              ) : (
                <>
                  <HiCheckCircle className="h-5 w-5" />
                  Submit
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default StageSubmissionForm;
